import { signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
	ColDef,
	GridApi,
	GridOptions,
	GridReadyEvent,
	SizeColumnsToContentStrategy,
	SizeColumnsToFitGridStrategy,
	SizeColumnsToFitProvidedWidthStrategy,
	StatusPanelDef
} from 'ag-grid-community';

import {
	AgGridFactory,
	CustomRendererColumnOptions,
	TextColumnOptions
} from '@shure/shared/angular/ui/components/ag-grid-next';

export type AutoSizeStrategy =
	| SizeColumnsToFitGridStrategy
	| SizeColumnsToFitProvidedWidthStrategy
	| SizeColumnsToContentStrategy;

export type AgGridStatusPanel =
	| 'agTotalRowCountComponent'
	| 'agSelectedRowCountComponent'
	| 'agFilteredRowCountComponent';
export const defaultAgGridStatusPanels: AgGridStatusPanel[] = [
	'agTotalRowCountComponent',
	'agSelectedRowCountComponent',
	'agFilteredRowCountComponent'
];

export abstract class BaseTableComponent<TRow> {
	protected readonly gridFactory: AgGridFactory<TRow>;

	/**
	 * Standard grid options
	 * https://ag-grid.com/angular-data-grid/grid-options/#reference-gridOptions-gridOptions
	 */
	public readonly gridOptions: GridOptions<TRow>;

	public gridApi?: GridApi<TRow>;

	public gridReady = signal(false);

	public statusBar: { statusPanels: StatusPanelDef[] } = { statusPanels: [] };

	constructor(protected readonly transloco: TranslocoService) {
		this.gridFactory = new AgGridFactory(transloco);
		this.gridOptions = this.getGridOptions();
	}

	public onGridReady(params: GridReadyEvent<TRow>): void {
		this.gridApi = params.api;
		this.gridReady.set(true);

		// During dev/testing, I noticed that when columns were removed the remaining columns would not
		// automatically resize proportionally unless I had first clicked on the "Reset columns" from the column menu.
		// By calling resetColumnStage once, when the grid is ready, and after a tick (hence the setTimeout()),
		// the resizing works as expected.
		setTimeout(() => {
			if (!this.gridApi?.isDestroyed()) {
				this.gridApi?.resetColumnState();
			}
		});
	}

	protected getGridOptions(): GridOptions<TRow> {
		return {
			...this.gridFactory.getDefaultGridOptions({
				allowExport: false,
				// Don't copy checkbox columns to the clipboard
				clipboardColumnFilter: (colDef) => !colDef.checkboxSelection
			}),
			rowSelection: 'multiple',
			selection: undefined
		};
	}

	protected getContextMenuItems(): string[] {
		return ['copy', 'copyWithHeaders', 'separator', 'export'];
	}

	/**
	 * @deprecated Use new AG Grid selection options instead
	 */
	protected createCheckboxColumn(options: Partial<ColDef<TRow>> = {}): ColDef<TRow> {
		return this.gridFactory.createCheckboxColumn(options);
	}

	/**
	 * Creates a column for the network status (offline, online, etc)
	 */
	protected createNetworkStatusColumn(options: Partial<ColDef<TRow>> = {}): ColDef<TRow> {
		return this.gridFactory.createNetworkStatusColumn({
			...options
		});
	}

	/**
	 * Creates a column definition for a basic text column with sorting and filtering enabled by default
	 */
	protected createTextColumn(options: TextColumnOptions<TRow>): ColDef<TRow> {
		return this.gridFactory.createTextColumn(options);
	}

	/**
	 * Creates a text column for an IP address field
	 */
	protected createIpAddressColumn(options: Omit<TextColumnOptions<TRow>, 'headerName'>): ColDef<TRow> {
		return this.gridFactory.createIpAddressColumn(options);
	}

	/**
	 * Creates a text column for a serial number field
	 */
	protected createSerialNumberColumn(options: Omit<TextColumnOptions<TRow>, 'headerName'>): ColDef<TRow> {
		return this.gridFactory.createSerialNumberColumn(options);
	}

	/**
	 * Creates a text column for a MAC address field
	 */
	protected createMacAddressColumn(options: Omit<TextColumnOptions<TRow>, 'headerName'>): ColDef<TRow> {
		return this.gridFactory.createMacAddressColumn(options);
	}

	/**
	 * Creates a column definition for a column with a custom cell renderer
	 */
	protected createCustomRendererColumn(options: CustomRendererColumnOptions<TRow>): ColDef<TRow> {
		return this.gridFactory.createCustomRendererColumn(options);
	}

	protected setStatusBar(panels: AgGridStatusPanel[]): void {
		this.statusBar = {
			statusPanels: panels.map((panel) => ({
				statusPanel: panel,
				align: 'left'
			}))
		};
	}
}
